import { logger } from '../utils/logger';
import type {
  ContentRecommendation,
  MasteryState,
  BehaviorPattern,
} from '@coffeeplexity/shared';

export interface ABTestExperiment {
  id: string;
  name: string;
  description: string;
  status: 'draft' | 'active' | 'paused' | 'completed';
  startDate: string;
  endDate?: string;
  targetAudience: AudienceSegment;
  variants: ABTestVariant[];
  metrics: ABTestMetric[];
  trafficAllocation: number; // 0-1, percentage of users to include
  minimumSampleSize: number;
  confidenceLevel: number; // e.g., 0.95 for 95%
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AudienceSegment {
  id: string;
  name: string;
  criteria: SegmentCriteria[];
  estimatedSize: number;
  description: string;
}

export interface SegmentCriteria {
  type:
    | 'skill_level'
    | 'journey_stage'
    | 'engagement_level'
    | 'content_preference'
    | 'behavior_pattern';
  operator:
    | 'equals'
    | 'not_equals'
    | 'greater_than'
    | 'less_than'
    | 'contains'
    | 'in'
    | 'not_in';
  value: any;
  weight: number;
}

export interface ABTestVariant {
  id: string;
  name: string;
  description: string;
  trafficPercentage: number; // 0-100
  configuration: VariantConfiguration;
  isControl: boolean;
  status: 'active' | 'paused';
}

export interface VariantConfiguration {
  algorithmType:
    | 'engagement_based'
    | 'mastery_based'
    | 'hybrid'
    | 'collaborative_filtering'
    | 'content_based';
  parameters: Record<string, any>;
  filteringRules: FilteringRule[];
  rankingWeights: RankingWeights;
  presentationStyle: PresentationStyle;
  explanationLevel: 'none' | 'basic' | 'detailed';
}

export interface FilteringRule {
  type: string;
  condition: string;
  action: 'include' | 'exclude' | 'boost' | 'penalize';
  strength: number;
}

export interface RankingWeights {
  relevance: number;
  engagement: number;
  difficulty: number;
  recency: number;
  social_proof: number;
  personalization: number;
}

export interface PresentationStyle {
  layout: 'list' | 'grid' | 'carousel' | 'mixed';
  cardStyle: 'minimal' | 'detailed' | 'visual';
  showConfidence: boolean;
  showExplanations: boolean;
  maxRecommendations: number;
}

export interface ABTestMetric {
  id: string;
  name: string;
  type: 'primary' | 'secondary' | 'guardrail';
  category: 'engagement' | 'conversion' | 'satisfaction' | 'retention';
  description: string;
  calculation: MetricCalculation;
  targetValue?: number;
  minimumDetectableEffect: number; // Minimum change to detect
}

export interface MetricCalculation {
  formula: string;
  events: string[];
  timeWindow: number; // hours
  aggregation: 'sum' | 'average' | 'count' | 'rate' | 'unique_count';
}

export interface ABTestAssignment {
  userId: string;
  experimentId: string;
  variantId: string;
  assignedAt: string;
  sessionId: string;
  context: AssignmentContext;
}

export interface AssignmentContext {
  userSegment: string;
  deviceType: string;
  appVersion: string;
  location?: string;
  timeOfDay: number;
  dayOfWeek: number;
}

export interface ABTestResult {
  experimentId: string;
  variantId: string;
  metrics: MetricResult[];
  sampleSize: number;
  conversionRate: number;
  confidenceInterval: ConfidenceInterval;
  statisticalSignificance: number;
  pValue: number;
  effect: EffectSize;
  generatedAt: string;
}

export interface MetricResult {
  metricId: string;
  value: number;
  variance: number;
  sampleSize: number;
  confidenceInterval: ConfidenceInterval;
}

export interface ConfidenceInterval {
  lower: number;
  upper: number;
  level: number; // e.g., 0.95
}

export interface EffectSize {
  absolute: number;
  relative: number; // percentage change
  cohensD?: number; // standardized effect size
}

export interface ABTestEvent {
  id: string;
  userId: string;
  experimentId: string;
  variantId: string;
  eventType: string;
  eventData: Record<string, any>;
  timestamp: string;
  sessionId: string;
}

export interface ExperimentAnalysis {
  experimentId: string;
  status: 'running' | 'completed' | 'inconclusive' | 'early_stop';
  results: ABTestResult[];
  winner?: string; // variant ID
  recommendation: AnalysisRecommendation;
  insights: ExperimentInsight[];
  generatedAt: string;
}

export interface AnalysisRecommendation {
  action: 'continue' | 'stop_early' | 'extend' | 'modify' | 'rollout_winner';
  reason: string;
  confidence: number;
  expectedImpact: string;
  risks: string[];
}

export interface ExperimentInsight {
  type: 'performance' | 'segment' | 'behavior' | 'anomaly';
  title: string;
  description: string;
  significance: 'high' | 'medium' | 'low';
  actionable: boolean;
  supportingData: Record<string, any>;
}

/**
 * Content Recommendation A/B Testing Service
 * Manages experiments, assignments, and analysis for content recommendation optimization
 */
export class ContentRecommendationABTestingService {
  private experiments: Map<string, ABTestExperiment> = new Map();
  private assignments: Map<string, ABTestAssignment[]> = new Map(); // userId -> assignments
  private events: Map<string, ABTestEvent[]> = new Map(); // experimentId -> events
  private results: Map<string, ABTestResult[]> = new Map(); // experimentId -> results

  /**
   * Create a new A/B test experiment
   */
  createExperiment(
    experiment: Omit<ABTestExperiment, 'id' | 'createdAt' | 'updatedAt'>
  ): ABTestExperiment {
    const id = `exp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const now = new Date().toISOString();

    const fullExperiment: ABTestExperiment = {
      ...experiment,
      id,
      createdAt: now,
      updatedAt: now,
    };

    // Validate experiment configuration
    this.validateExperiment(fullExperiment);

    this.experiments.set(id, fullExperiment);
    logger.log('A/B test experiment created:', fullExperiment);

    return fullExperiment;
  }

  /**
   * Get user assignment for active experiments
   */
  getUserAssignment(
    userId: string,
    masteryStates: MasteryState[],
    behaviorPatterns: BehaviorPattern[],
    context: Partial<AssignmentContext> = {}
  ): ABTestAssignment[] {
    const assignments: ABTestAssignment[] = [];
    const activeExperiments = Array.from(this.experiments.values()).filter(
      exp => exp.status === 'active'
    );

    for (const experiment of activeExperiments) {
      // Check if user is already assigned to this experiment
      const existingAssignment = this.getExistingAssignment(
        userId,
        experiment.id
      );
      if (existingAssignment) {
        assignments.push(existingAssignment);
        continue;
      }

      // Check if user matches target audience
      if (
        !this.matchesAudience(
          userId,
          masteryStates,
          behaviorPatterns,
          experiment.targetAudience
        )
      ) {
        continue;
      }

      // Check traffic allocation
      if (Math.random() > experiment.trafficAllocation) {
        continue;
      }

      // Assign user to variant
      const variant = this.assignToVariant(userId, experiment);
      if (variant) {
        const assignment: ABTestAssignment = {
          userId,
          experimentId: experiment.id,
          variantId: variant.id,
          assignedAt: new Date().toISOString(),
          sessionId: this.generateSessionId(userId),
          context: {
            userSegment: this.getUserSegment(masteryStates, behaviorPatterns),
            deviceType: context.deviceType || 'mobile',
            appVersion: context.appVersion || '1.0.0',
            location: context.location,
            timeOfDay: new Date().getHours(),
            dayOfWeek: new Date().getDay(),
          },
        };

        // Store assignment
        if (!this.assignments.has(userId)) {
          this.assignments.set(userId, []);
        }
        this.assignments.get(userId)!.push(assignment);
        assignments.push(assignment);

        logger.log('User assigned to A/B test:', assignment);
      }
    }

    return assignments;
  }

  /**
   * Apply A/B test variant to content recommendations
   */
  applyVariant(
    recommendations: ContentRecommendation[],
    variantId: string,
    experimentId: string
  ): ContentRecommendation[] {
    const experiment = this.experiments.get(experimentId);
    if (!experiment) {
      logger.warn('Experiment not found:', experimentId);
      return recommendations;
    }

    const variant = experiment.variants.find(v => v.id === variantId);
    if (!variant) {
      logger.warn('Variant not found:', variantId);
      return recommendations;
    }

    try {
      return this.transformRecommendations(
        recommendations,
        variant.configuration
      );
    } catch (error) {
      logger.error('Error applying A/B test variant:', error);
      return recommendations;
    }
  }

  /**
   * Track A/B test event
   */
  trackEvent(
    userId: string,
    eventType: string,
    eventData: Record<string, any> = {},
    sessionId?: string
  ): void {
    const userAssignments = this.assignments.get(userId) || [];

    for (const assignment of userAssignments) {
      const event: ABTestEvent = {
        id: `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        userId,
        experimentId: assignment.experimentId,
        variantId: assignment.variantId,
        eventType,
        eventData,
        timestamp: new Date().toISOString(),
        sessionId: sessionId || assignment.sessionId,
      };

      // Store event
      if (!this.events.has(assignment.experimentId)) {
        this.events.set(assignment.experimentId, []);
      }
      this.events.get(assignment.experimentId)!.push(event);

      logger.log('A/B test event tracked:', event);
    }
  }

  /**
   * Analyze experiment results
   */
  analyzeExperiment(experimentId: string): ExperimentAnalysis | null {
    const experiment = this.experiments.get(experimentId);
    if (!experiment) {
      return null;
    }

    const events = this.events.get(experimentId) || [];
    if (events.length === 0) {
      return {
        experimentId,
        status: 'running',
        results: [],
        recommendation: {
          action: 'continue',
          reason: 'Insufficient data for analysis',
          confidence: 0,
          expectedImpact: 'Unknown',
          risks: ['Low sample size'],
        },
        insights: [],
        generatedAt: new Date().toISOString(),
      };
    }

    // Calculate results for each variant
    const results: ABTestResult[] = [];
    for (const variant of experiment.variants) {
      const variantEvents = events.filter(e => e.variantId === variant.id);
      const result = this.calculateVariantResults(
        variant,
        variantEvents,
        experiment.metrics
      );
      results.push(result);
    }

    // Determine statistical significance and winner
    const analysis = this.performStatisticalAnalysis(results, experiment);

    return {
      experimentId,
      status: this.determineExperimentStatus(analysis, experiment),
      results,
      winner: analysis.winner,
      recommendation: this.generateRecommendation(analysis, experiment),
      insights: this.generateInsights(results, events, experiment),
      generatedAt: new Date().toISOString(),
    };
  }

  /**
   * Get experiment performance summary
   */
  getExperimentSummary(experimentId: string): {
    experiment: ABTestExperiment;
    totalUsers: number;
    totalEvents: number;
    variantDistribution: Record<string, number>;
    topMetrics: MetricResult[];
  } | null {
    const experiment = this.experiments.get(experimentId);
    if (!experiment) {
      return null;
    }

    const events = this.events.get(experimentId) || [];
    const uniqueUsers = new Set(events.map(e => e.userId)).size;

    // Calculate variant distribution
    const variantCounts: Record<string, number> = {};
    const userVariants = new Map<string, string>();

    events.forEach(event => {
      userVariants.set(event.userId, event.variantId);
    });

    userVariants.forEach(variantId => {
      variantCounts[variantId] = (variantCounts[variantId] || 0) + 1;
    });

    // Get top metrics (placeholder implementation)
    const topMetrics: MetricResult[] = experiment.metrics
      .slice(0, 3)
      .map(metric => ({
        metricId: metric.id,
        value: Math.random() * 100, // Placeholder
        variance: Math.random() * 10,
        sampleSize: uniqueUsers,
        confidenceInterval: {
          lower: Math.random() * 50,
          upper: Math.random() * 50 + 50,
          level: 0.95,
        },
      }));

    return {
      experiment,
      totalUsers: uniqueUsers,
      totalEvents: events.length,
      variantDistribution: variantCounts,
      topMetrics,
    };
  }

  /**
   * Stop experiment early
   */
  stopExperiment(experimentId: string, reason: string): boolean {
    const experiment = this.experiments.get(experimentId);
    if (!experiment) {
      return false;
    }

    experiment.status = 'completed';
    experiment.endDate = new Date().toISOString();
    experiment.updatedAt = new Date().toISOString();

    logger.log(`Experiment ${experimentId} stopped early: ${reason}`);
    return true;
  }

  /**
   * Get all active experiments
   */
  getActiveExperiments(): ABTestExperiment[] {
    return Array.from(this.experiments.values()).filter(
      exp => exp.status === 'active'
    );
  }

  /**
   * Validate experiment configuration
   */
  private validateExperiment(experiment: ABTestExperiment): void {
    // Check traffic allocation
    const totalTraffic = experiment.variants.reduce(
      (sum, v) => sum + v.trafficPercentage,
      0
    );
    if (Math.abs(totalTraffic - 100) > 0.01) {
      throw new Error('Variant traffic percentages must sum to 100%');
    }

    // Check for control variant
    const controlVariants = experiment.variants.filter(v => v.isControl);
    if (controlVariants.length !== 1) {
      throw new Error('Experiment must have exactly one control variant');
    }

    // Validate metrics
    if (experiment.metrics.length === 0) {
      throw new Error('Experiment must have at least one metric');
    }

    const primaryMetrics = experiment.metrics.filter(m => m.type === 'primary');
    if (primaryMetrics.length === 0) {
      throw new Error('Experiment must have at least one primary metric');
    }
  }

  /**
   * Check if user matches audience criteria
   */
  private matchesAudience(
    userId: string,
    masteryStates: MasteryState[],
    behaviorPatterns: BehaviorPattern[],
    audience: AudienceSegment
  ): boolean {
    // Simple implementation - in production, this would be more sophisticated
    for (const criteria of audience.criteria) {
      if (!this.evaluateCriteria(criteria, masteryStates, behaviorPatterns)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Evaluate audience criteria
   */
  private evaluateCriteria(
    criteria: SegmentCriteria,
    masteryStates: MasteryState[],
    behaviorPatterns: BehaviorPattern[]
  ): boolean {
    // Placeholder implementation
    switch (criteria.type) {
      case 'skill_level':
        const avgMastery =
          masteryStates.length > 0
            ? masteryStates.reduce((sum, m) => sum + m.masteryLevel, 0) /
              masteryStates.length
            : 0.5;
        return this.compareValues(
          avgMastery,
          criteria.operator,
          criteria.value
        );

      case 'engagement_level':
        // Would calculate from behavior patterns
        return Math.random() > 0.5; // Placeholder

      default:
        return true;
    }
  }

  /**
   * Compare values based on operator
   */
  private compareValues(actual: any, operator: string, expected: any): boolean {
    switch (operator) {
      case 'equals':
        return actual === expected;
      case 'not_equals':
        return actual !== expected;
      case 'greater_than':
        return actual > expected;
      case 'less_than':
        return actual < expected;
      case 'contains':
        return String(actual).includes(String(expected));
      case 'in':
        return Array.isArray(expected) && expected.includes(actual);
      case 'not_in':
        return Array.isArray(expected) && !expected.includes(actual);
      default:
        return false;
    }
  }

  /**
   * Get existing assignment for user and experiment
   */
  private getExistingAssignment(
    userId: string,
    experimentId: string
  ): ABTestAssignment | null {
    const userAssignments = this.assignments.get(userId) || [];
    return userAssignments.find(a => a.experimentId === experimentId) || null;
  }

  /**
   * Assign user to variant using consistent hashing
   */
  private assignToVariant(
    userId: string,
    experiment: ABTestExperiment
  ): ABTestVariant | null {
    // Use consistent hashing to ensure same user always gets same variant
    const hash = this.hashString(`${userId}_${experiment.id}`);
    const hashValue = hash % 100; // 0-99

    let cumulativePercentage = 0;
    for (const variant of experiment.variants) {
      cumulativePercentage += variant.trafficPercentage;
      if (hashValue < cumulativePercentage) {
        return variant;
      }
    }

    return null;
  }

  /**
   * Simple hash function for consistent assignment
   */
  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Transform recommendations based on variant configuration
   */
  private transformRecommendations(
    recommendations: ContentRecommendation[],
    config: VariantConfiguration
  ): ContentRecommendation[] {
    let transformed = [...recommendations];

    // Apply filtering rules
    for (const rule of config.filteringRules) {
      transformed = this.applyFilteringRule(transformed, rule);
    }

    // Apply ranking weights
    transformed = this.applyRankingWeights(transformed, config.rankingWeights);

    // Apply presentation limits
    transformed = transformed.slice(
      0,
      config.presentationStyle.maxRecommendations
    );

    // Add variant-specific metadata
    transformed = transformed.map(rec => ({
      ...rec,
      metadata: {
        ...rec.metadata,
        abTestVariant: config,
        showConfidence: config.presentationStyle.showConfidence,
        showExplanations: config.presentationStyle.showExplanations,
      },
    }));

    return transformed;
  }

  /**
   * Apply filtering rule to recommendations
   */
  private applyFilteringRule(
    recommendations: ContentRecommendation[],
    rule: FilteringRule
  ): ContentRecommendation[] {
    // Simplified rule application
    switch (rule.action) {
      case 'include':
        return recommendations.filter(rec =>
          this.evaluateRuleCondition(rec, rule.condition)
        );
      case 'exclude':
        return recommendations.filter(
          rec => !this.evaluateRuleCondition(rec, rule.condition)
        );
      case 'boost':
        return recommendations.map(rec => {
          if (this.evaluateRuleCondition(rec, rule.condition)) {
            return { ...rec, priority: rec.priority * (1 + rule.strength) };
          }
          return rec;
        });
      case 'penalize':
        return recommendations.map(rec => {
          if (this.evaluateRuleCondition(rec, rule.condition)) {
            return { ...rec, priority: rec.priority * (1 - rule.strength) };
          }
          return rec;
        });
      default:
        return recommendations;
    }
  }

  /**
   * Evaluate rule condition (simplified)
   */
  private evaluateRuleCondition(
    recommendation: ContentRecommendation,
    condition: string
  ): boolean {
    // Simplified condition evaluation
    try {
      // In production, use a proper expression evaluator
      if (condition.includes('contentType')) {
        const match = condition.match(/contentType\s*===?\s*['"]([^'"]+)['"]/);
        return match ? recommendation.contentType === match[1] : false;
      }
      if (condition.includes('difficultyLevel')) {
        const match = condition.match(/difficultyLevel\s*([<>=]+)\s*(\d+)/);
        if (match) {
          const operator = match[1];
          const value = parseInt(match[2]);
          switch (operator) {
            case '<':
              return recommendation.difficultyLevel < value;
            case '>':
              return recommendation.difficultyLevel > value;
            case '<=':
              return recommendation.difficultyLevel <= value;
            case '>=':
              return recommendation.difficultyLevel >= value;
            case '==':
              return recommendation.difficultyLevel === value;
            default:
              return false;
          }
        }
      }
      return false;
    } catch (error) {
      logger.warn('Error evaluating rule condition:', condition, error);
      return false;
    }
  }

  /**
   * Apply ranking weights to recommendations
   */
  private applyRankingWeights(
    recommendations: ContentRecommendation[],
    weights: RankingWeights
  ): ContentRecommendation[] {
    return recommendations
      .map(rec => {
        // Calculate weighted score
        const relevanceScore = rec.confidence || 0.5;
        const engagementScore = rec.metadata?.engagementScore || 0.5;
        const difficultyScore = 1 - Math.abs(rec.difficultyLevel / 10 - 0.5); // Prefer moderate difficulty
        const recencyScore = this.calculateRecencyScore(rec);
        const socialProofScore = this.calculateSocialProofScore(rec);
        const personalizationScore = rec.priority || 0.5;

        const weightedScore =
          relevanceScore * weights.relevance +
          engagementScore * weights.engagement +
          difficultyScore * weights.difficulty +
          recencyScore * weights.recency +
          socialProofScore * weights.social_proof +
          personalizationScore * weights.personalization;

        return {
          ...rec,
          priority: weightedScore,
          metadata: {
            ...rec.metadata,
            weightedScore,
            rankingWeights: weights,
          },
        };
      })
      .sort((a, b) => b.priority - a.priority);
  }

  /**
   * Calculate recency score
   */
  private calculateRecencyScore(recommendation: ContentRecommendation): number {
    const now = new Date();
    const contentDate = new Date(recommendation.createdAt);
    const daysSinceCreation =
      (now.getTime() - contentDate.getTime()) / (1000 * 60 * 60 * 24);
    return Math.exp(-daysSinceCreation / 14); // 14-day half-life
  }

  /**
   * Calculate social proof score
   */
  private calculateSocialProofScore(
    recommendation: ContentRecommendation
  ): number {
    const rating = recommendation.metadata?.rating || 0;
    const reviewCount = recommendation.metadata?.reviewCount || 0;
    const viewCount = recommendation.metadata?.viewCount || 0;

    const ratingScore = rating / 5;
    const reviewScore = Math.min(1, reviewCount / 50);
    const popularityScore = Math.min(1, viewCount / 1000);

    return ratingScore * 0.5 + reviewScore * 0.3 + popularityScore * 0.2;
  }

  /**
   * Calculate variant results
   */
  private calculateVariantResults(
    variant: ABTestVariant,
    events: ABTestEvent[],
    metrics: ABTestMetric[]
  ): ABTestResult {
    const uniqueUsers = new Set(events.map(e => e.userId)).size;

    // Calculate metrics (simplified)
    const metricResults: MetricResult[] = metrics.map(metric => ({
      metricId: metric.id,
      value: Math.random() * 100, // Placeholder calculation
      variance: Math.random() * 10,
      sampleSize: uniqueUsers,
      confidenceInterval: {
        lower: Math.random() * 50,
        upper: Math.random() * 50 + 50,
        level: 0.95,
      },
    }));

    return {
      experimentId: '', // Will be set by caller
      variantId: variant.id,
      metrics: metricResults,
      sampleSize: uniqueUsers,
      conversionRate: Math.random() * 0.3, // Placeholder
      confidenceInterval: {
        lower: 0.1,
        upper: 0.4,
        level: 0.95,
      },
      statisticalSignificance: Math.random(),
      pValue: Math.random() * 0.1,
      effect: {
        absolute: Math.random() * 10,
        relative: Math.random() * 20,
        cohensD: Math.random() * 0.5,
      },
      generatedAt: new Date().toISOString(),
    };
  }

  /**
   * Perform statistical analysis
   */
  private performStatisticalAnalysis(
    results: ABTestResult[],
    experiment: ABTestExperiment
  ): { winner?: string; significant: boolean; confidence: number } {
    // Simplified statistical analysis
    if (results.length < 2) {
      return { significant: false, confidence: 0 };
    }

    const controlResult = results.find(
      r => experiment.variants.find(v => v.id === r.variantId)?.isControl
    );

    if (!controlResult) {
      return { significant: false, confidence: 0 };
    }

    // Find best performing variant
    const bestResult = results.reduce((best, current) =>
      current.conversionRate > best.conversionRate ? current : best
    );

    const isSignificant =
      bestResult.pValue < 0.05 &&
      bestResult.sampleSize >= experiment.minimumSampleSize;
    const winner =
      isSignificant && bestResult.variantId !== controlResult.variantId
        ? bestResult.variantId
        : undefined;

    return {
      winner,
      significant: isSignificant,
      confidence: 1 - bestResult.pValue,
    };
  }

  /**
   * Determine experiment status
   */
  private determineExperimentStatus(
    analysis: { winner?: string; significant: boolean; confidence: number },
    experiment: ABTestExperiment
  ): 'running' | 'completed' | 'inconclusive' | 'early_stop' {
    if (experiment.status === 'completed') {
      return 'completed';
    }

    if (analysis.significant && analysis.winner) {
      return 'early_stop'; // Can stop early with significant results
    }

    if (experiment.endDate && new Date() > new Date(experiment.endDate)) {
      return analysis.significant ? 'completed' : 'inconclusive';
    }

    return 'running';
  }

  /**
   * Generate recommendation based on analysis
   */
  private generateRecommendation(
    analysis: { winner?: string; significant: boolean; confidence: number },
    experiment: ABTestExperiment
  ): AnalysisRecommendation {
    if (analysis.significant && analysis.winner) {
      return {
        action: 'rollout_winner',
        reason: `Variant ${analysis.winner} shows statistically significant improvement`,
        confidence: analysis.confidence,
        expectedImpact: 'Positive impact on key metrics',
        risks: ['Implementation complexity', 'Long-term effects unknown'],
      };
    }

    if (
      !analysis.significant &&
      experiment.endDate &&
      new Date() > new Date(experiment.endDate)
    ) {
      return {
        action: 'stop_early',
        reason: 'No significant difference found after planned duration',
        confidence: 0.8,
        expectedImpact: 'No change expected',
        risks: ['Missed opportunity for improvement'],
      };
    }

    return {
      action: 'continue',
      reason: 'Experiment is still collecting data',
      confidence: 0.6,
      expectedImpact: 'Results pending',
      risks: ['Opportunity cost of delayed decision'],
    };
  }

  /**
   * Generate insights from experiment data
   */
  private generateInsights(
    results: ABTestResult[],
    events: ABTestEvent[],
    experiment: ABTestExperiment
  ): ExperimentInsight[] {
    const insights: ExperimentInsight[] = [];

    // Performance insight
    if (results.length > 1) {
      const bestVariant = results.reduce((best, current) =>
        current.conversionRate > best.conversionRate ? current : best
      );

      insights.push({
        type: 'performance',
        title: 'Best Performing Variant',
        description: `Variant ${bestVariant.variantId} has the highest conversion rate at ${(bestVariant.conversionRate * 100).toFixed(1)}%`,
        significance: 'high',
        actionable: true,
        supportingData: {
          variantId: bestVariant.variantId,
          conversionRate: bestVariant.conversionRate,
        },
      });
    }

    // Sample size insight
    const totalSampleSize = results.reduce((sum, r) => sum + r.sampleSize, 0);
    if (totalSampleSize < experiment.minimumSampleSize) {
      insights.push({
        type: 'performance',
        title: 'Insufficient Sample Size',
        description: `Current sample size (${totalSampleSize}) is below minimum required (${experiment.minimumSampleSize})`,
        significance: 'medium',
        actionable: true,
        supportingData: {
          currentSize: totalSampleSize,
          requiredSize: experiment.minimumSampleSize,
        },
      });
    }

    return insights;
  }

  /**
   * Generate session ID
   */
  private generateSessionId(userId: string): string {
    return `session_${userId}_${Date.now()}`;
  }

  /**
   * Get user segment (simplified)
   */
  private getUserSegment(
    masteryStates: MasteryState[],
    behaviorPatterns: BehaviorPattern[]
  ): string {
    const avgMastery =
      masteryStates.length > 0
        ? masteryStates.reduce((sum, m) => sum + m.masteryLevel, 0) /
          masteryStates.length
        : 0.5;

    if (avgMastery < 0.3) return 'beginner';
    if (avgMastery < 0.7) return 'intermediate';
    return 'advanced';
  }
}

// Export singleton instance
export const contentRecommendationABTesting =
  new ContentRecommendationABTestingService();
